import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Linkedin, Facebook, Menu, X } from "lucide-react";
import { XLogo } from "@/components/features/landing/XLogo";
import { BlueSkyLogo } from "@/components/features/landing/BlueSkyLogo";
import { SignInButton } from "@clerk/nextjs";
import { Button } from "@/components/ui/Button";

export function PublicLayout({ children }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close mobile menu when route changes or ESC key is pressed
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === "Escape") {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscKey);
    return () => window.removeEventListener("keydown", handleEscKey);
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex-shrink-0">
            <Link
              href="/"
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              <Image
                src="/svgs/logo.svg"
                alt="PureblueHub Logo"
                width={180}
                height={40}
                className="hover:cursor-pointer"
                priority
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {/* Social media links */}
            <div className="flex items-center space-x-4">
              <Link
                href="/blogs"
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                Blog
              </Link>

              {/* Social media links */}
              <Link
                href="https://www.linkedin.com/company/freetalentassessment/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Linkedin className="w-6 h-6 text-gray-500 hover:text-blue-600" />
              </Link>

              <Link
                href="https://www.facebook.com/profile.php?id=61572233444141&__cft__[0]=AZWfdE56SjlLK5gbBazhGSd1bgLfjK7_jXzmbXJ3xxC3WhzCjMhgPK--5a5y8IscXSl2NOyerk6npEPNt1Kz5BAj8InKudsa3u1Q7s2k2FGa8I8XTCSssobPTEFuzZPCLn-GMvuv1hDwcrCN59IBlSKxNSpHgYD3-3wYeYsnUt-1BFerXB5zZqrmKxIw41WYD3SRbk_yNcrPIii3BRZEW9_q&__tn__=-]C%2CP-R"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Facebook className="w-6 h-6 text-gray-500 hover:text-blue-600" />
              </Link>

              <Link
                href="https://x.com/talent_assess"
                target="_blank"
                rel="noopener noreferrer"
              >
                <XLogo />
              </Link>

              <Link
                href="https://bsky.app/profile/freetalentassess.bsky.social"
                target="_blank"
                rel="noopener noreferrer"
              >
                <BlueSkyLogo />
              </Link>

              <SignInButton mode="modal" forceRedirectUrl="/dashboard">
                <Button
                  variant="primary"
                  size="md"
                  className="w-full sm:w-auto"
                >
                  Login
                </Button>
              </SignInButton>
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-gray-600"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-white pt-16">
          <div className="p-4 space-y-6">
            <Link
              href="/blogs"
              className="block text-lg font-medium text-gray-700 hover:text-blue-600 py-2"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Blog
            </Link>

            <SignInButton mode="modal" forceRedirectUrl="/dashboard">
              <Button variant="primary" size="md" className="w-full sm:w-auto">
                Login
              </Button>
            </SignInButton>

            {/* Social media links */}
            <div className="flex items-center space-x-6 py-4">
              <Link
                href="https://www.linkedin.com/company/pureblue-hub/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Linkedin className="w-6 h-6 text-gray-500 hover:text-blue-600" />
              </Link>

              <Link
                href="https://www.facebook.com/profile.php?id=61572627987380"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Facebook className="w-6 h-6 text-gray-500 hover:text-blue-600" />
              </Link>

              <Link
                href="https://x.com/purebluehub"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X (Twitter)"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <XLogo className="text-gray-500 hover:text-blue-600" />
              </Link>

              <Link
                href="https://bsky.app/profile/purebluehub.bsky.social"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="BlueSky"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <BlueSkyLogo className="text-gray-500 hover:text-blue-600" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center">
          <div className="mb-4 md:mb-0">
            <Image
              src="/svgs/logo.svg"
              alt="PureblueHub Logo"
              width={150}
              height={35}
              className="mb-3"
            />
            <p className="text-gray-600 text-sm">
              Copyright © {new Date().getFullYear()} AiLead, Inc. dba
              Pureblue.ai
            </p>
          </div>

          <div className="flex flex-col items-center md:items-end">
            <div className="flex space-x-4 mb-3">
              <Link
                href="https://www.linkedin.com/company/freetalentassessment/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5 text-gray-500 hover:text-blue-600" />
              </Link>

              <Link
                href="https://www.facebook.com/profile.php?id=61572627987380"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5 text-gray-500 hover:text-blue-600" />
              </Link>

              <Link
                href="https://x.com/talent_assess"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X (Twitter)"
              >
                <XLogo className="text-gray-500 hover:text-blue-600" />
              </Link>

              <Link
                href="https://bsky.app/profile/freetalentassess.bsky.social"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="BlueSky"
              >
                <BlueSkyLogo className="text-gray-500 hover:text-blue-600" />
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
